/**
 * Store-account subscription detection (ground truth for the Restore lead).
 *
 * Thin, mockable seam over the StoreEntitlements Capacitor plugin: does the
 * phone's signed-in STORE account (Apple ID / Google Play) hold an active
 * subscription for this app, independent of the current Firebase uid?
 *
 * This supersedes guessing from the device-local orphaned-purchase hint
 * (lib/paidLaunch.ts) as the primary signal: the hint dies on reinstall or
 * cleared storage, while the store query is ground truth on every launch. The
 * hint remains as the instant-at-mount fast path and the fallback for when
 * this query can't answer (billing service unavailable).
 *
 * OWNERSHIP RULE: any active subscription counts as a membership. Both store
 * queries are scoped to THIS app, and every subscription SKU we have ever sold
 * (spending_tools_monthly / spending_tools_annual, and the legacy data_sync
 * products) maps to a paid line in the entitlement source of truth — so a
 * static SKU allowlist here could only ever FALSE-NEGATIVE (e.g. a legacy
 * subscriber on a new device, exactly who Restore should serve). The raw ids
 * ride along for analytics and future product-line decisions.
 */
import { Capacitor } from '@capacitor/core';
import { logWarn } from '@/lib/log';
import StoreEntitlements, { type StoreEntitlementsResult } from '../plugins/StoreEntitlements';

/**
 * Query the device store account's active subscriptions for this app.
 * Never rejects: web, plugin errors, and billing-unavailable all resolve to
 * `{ supported: false, productIds: [] }` — UNKNOWN, not "owns nothing" —
 * so callers degrade to exactly the pre-plugin behavior.
 */
export const getStoreOwnedSubscriptions = async (): Promise<StoreEntitlementsResult> => {
  if (!Capacitor.isNativePlatform()) return { supported: false, productIds: [] };
  try {
    const result = await StoreEntitlements.getStoreEntitlements();
    return { supported: !!result?.supported, productIds: result?.productIds ?? [] };
  } catch (err) {
    logWarn('[StoreEntitlements] store query failed — ownership unknown', err);
    return { supported: false, productIds: [] };
  }
};

/** True when the store account verifiably owns an active subscription for this app. */
export const storeOwnsSubscription = (result: StoreEntitlementsResult): boolean =>
  result.supported && result.productIds.length > 0;
